import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Ativo': 'bg-emerald-100 text-emerald-700',
  'Férias': 'bg-blue-100 text-blue-700',
  'Afastado': 'bg-amber-100 text-amber-700',
  'Desligado': 'bg-red-100 text-red-700'
};

export function EmployeesList() {
  const nav = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    load();
  }, [search, statusFilter]);

  const load = async () => {
    try {
      const { data } = await api.get('/employees', { params: { search, status: statusFilter } });
      setEmployees(data.items || []);
    } catch (err) {
      toast.error('Erro ao listar funcionários.');
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Funcionários</h1>
          <p className="text-slate-500">Gerenciamento de colaboradores.</p>
        </div>
        <Button onClick={() => nav('/rh/funcionarios/novo')} className="bg-[#C52429] hover:bg-[#A81E23] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Novo Funcionário
        </Button>
      </div>

      <div className="bg-white p-4 rounded-xl border flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input 
            type="text" 
            placeholder="Buscar por nome, CPF, matrícula..."
            className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C52429] outline-none"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="w-full md:w-64 flex gap-2">
          <Filter className="w-4 h-4 text-slate-400 mt-3" />
          <select 
            className="w-full border rounded-lg px-3 py-2 outline-none"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">Todos os status</option>
            <option value="Ativo">Ativo</option>
            <option value="Férias">Férias</option>
            <option value="Afastado">Afastado</option>
            <option value="Desligado">Desligado</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-600 font-semibold border-b">
              <tr>
                <th className="py-3 px-4">Funcionário</th>
                <th className="py-3 px-4">Matrícula</th>
                <th className="py-3 px-4">Cargo / Setor</th>
                <th className="py-3 px-4">Admissão</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-slate-500">Nenhum funcionário encontrado.</td>
                </tr>
              ) : employees.map(emp => (
                <tr key={emp.id} className="hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-500 overflow-hidden shrink-0">
                        {emp.photo_url ? (
                          <img src={emp.photo_url} alt={emp.name} className="w-full h-full object-cover" />
                        ) : (
                          emp.name ? emp.name.charAt(0).toUpperCase() : '?'
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.cpf || 'Sem CPF'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs">{emp.professional?.matricula || '-'}</td>
                  <td className="py-3 px-4">
                    <div className="text-slate-800">{emp.professional?.role || '-'}</div>
                    <div className="text-xs text-slate-500">{emp.professional?.department || '-'}</div>
                  </td>
                  <td className="py-3 px-4">{emp.professional?.admission_date || '-'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${emp.status === 'Ativo' ? 'bg-emerald-100 text-emerald-700' : emp.status === 'Desligado' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {emp.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => nav(`/rh/funcionarios/${emp.id}`)}>
                      Perfil
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
